/**
 * @vuelto-surface
 * mobile: ok
 * desktop: n/a
 * strategy: inherit
 */
import React from 'react';
import { TicketListRow } from '../../shared/ticket/TicketListRow';
import type { SaleListRow } from '../domain/salesTypes';
import {
  buildSaleListMeta,
  formatSaleChannelLabel,
  formatSaleDisplayRef,
  formatSaleListTimestamp,
  formatSaleMoney,
  formatSalePendingLine,
  resolveSaleListActionablePill,
} from '../application/salePresentation';

export type SaleListRowCardVariant = 'default' | 'fiado';

export type SaleListRowCardProps = {
  row: SaleListRow;
  onPress: () => void;
  selected?: boolean;
  variant?: SaleListRowCardVariant;
  customerPendingAmount?: number;
  customerPaidPercent?: number;
  paymentMethodNames?: Record<string, string>;
  diningTableName?: string | null;
  accessibilityLabel?: string;
};

function safeTotal(value: number | null | undefined): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function SaleListRowCardInner({
  row,
  onPress,
  variant = 'default',
  customerPendingAmount,
  customerPaidPercent,
  paymentMethodNames = {},
  diningTableName,
  accessibilityLabel,
}: SaleListRowCardProps) {
  const amountLabel = formatSaleMoney(safeTotal(row.total));
  const dateLabel = formatSaleListTimestamp(row.createdAt);
  const channelLabel = formatSaleChannelLabel(row.channel);
  const isVoided = row.status === 'voided';

  if (variant === 'fiado') {
    const pendingLine =
      customerPendingAmount != null
        ? `Pendiente ${formatSaleMoney(customerPendingAmount)}${
            customerPaidPercent != null ? ` · ${Math.round(customerPaidPercent)}% pagado` : ''
          }`
        : formatSalePendingLine(row.pendingAmount, row.paymentStatus) ?? 'Sin saldo pendiente';
    return (
      <TicketListRow
        title={amountLabel}
        subtitle={dateLabel}
        meta={pendingLine}
        statusLabel={isVoided ? 'Anulada' : null}
        statusTone="bad"
        inactive={isVoided}
        onPress={onPress}
        accessibilityLabel={accessibilityLabel ?? `Venta ${amountLabel}, ${dateLabel}`}
      />
    );
  }

  const pill = resolveSaleListActionablePill(row);
  const refLabel = formatSaleDisplayRef({ saleId: row.id, counterNumber: row.counterNumber });
  const meta = buildSaleListMeta({
    row,
    paymentMethodNames,
    diningTableName,
  });

  return (
    <TicketListRow
      title={`${refLabel} · ${amountLabel}`}
      subtitle={`${dateLabel} · ${channelLabel}`}
      meta={meta}
      statusLabel={pill?.label ?? null}
      statusTone={pill?.tone}
      inactive={isVoided}
      onPress={onPress}
      accessibilityLabel={
        accessibilityLabel ?? `Venta ${channelLabel} ${amountLabel}, ${dateLabel}`
      }
    />
  );
}

export const SaleListRowCard = React.memo(SaleListRowCardInner);
